<template>
  <div class="oa-dashboard">
    <!-- ===== 第一行：统计卡片 ===== -->
    <div class="stats-row">
      <div class="stat-card bg-blue">
        <div class="stat-left">
          <div class="stat-icon"><i class="fas fa-clipboard-check"></i></div>
        </div>
        <div class="stat-right">
          <div class="stat-num">{{ todoCount }}</div>
          <div class="stat-title">待办审批</div>
        </div>
        <div class="stat-badge urgent" v-if="urgentCount > 0">{{ urgentCount }}项紧急</div>
      </div>
      <div class="stat-card bg-green">
        <div class="stat-left">
          <div class="stat-icon"><i class="fas fa-calendar-day"></i></div>
        </div>
        <div class="stat-right">
          <div class="stat-num">{{ todayScheduleCount }}</div>
          <div class="stat-title">今日日程</div>
        </div>
        <div class="stat-badge">{{ todayShort }}</div>
      </div>
      <div class="stat-card shortcuts-card">
        <div class="shortcut-card-body">
          <div class="shortcut-card-grid">
            <div v-for="s in shortcuts.slice(0, 6)" :key="s.guid" class="sc-item" @click="navigateTo(s.path, s)" :title="s.title">
              <div class="sc-icon"><i :class="s.icon || 'fas fa-link'"></i></div>
              <span>{{ s.title }}</span>
            </div>
          </div>
          <button class="sc-expand-btn" @click.stop="shortcutFolded = false" title="展开全部">
            <i class="fas fa-th-large"></i> 展开
          </button>
        </div>
        <!-- 展开弹窗 -->
        <Teleport to="body">
          <div v-if="!shortcutFolded" class="shortcut-overlay" @click="shortcutFolded = true">
            <div class="shortcut-popup" @click.stop>
              <div class="popup-head">
                <h4><i class="fas fa-th-large"></i> 快捷入口</h4>
                <button @click="shortcutFolded = true"><i class="fas fa-times"></i></button>
              </div>
              <div class="popup-body">
                <div class="shortcut-grid-expanded">
                  <div v-for="s in shortcuts" :key="s.guid" class="sc-item" @click="navigateTo(s.path, s); shortcutFolded = true" :title="s.title">
                    <div class="sc-icon"><i :class="s.icon || 'fas fa-link'"></i></div>
                    <span>{{ s.title }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Teleport>
      </div>
    </div>

    <!-- ===== 第二行：新闻公告 + 系统公告 + 我的申请 三列 ===== -->
    <div class="three-col-row">
      <!-- 系统公告 -->
      <div class="panel sys-panel">
        <div class="panel-head">
          <h4><i class="fas fa-bullhorn"></i> 系统公告</h4>
          <button class="link-more" @click="navigateTo('announcements')">更多 <i class="fas fa-angle-right"></i></button>
        </div>
        <div class="panel-body">
          <div v-if="newsLoading" class="loading-row">加载中...</div>
          <div v-else-if="sysNotices.length === 0" class="empty"><span>暂无系统公告</span></div>
          <div v-for="s in sysNotices" :key="s.guid" class="sys-row">
            <span class="sys-tag" :class="getNoticeLevel(s)">{{ getNoticeLabel(s) }}</span>
            <span class="sys-title">{{ s.titles || s.Titles }}</span>
            <span class="sys-date">{{ fmtDate(s.publicTime || s.PublicTime) }}</span>
          </div>
        </div>
      </div>
      <!-- 新闻公告 -->
      <div class="panel news-panel">
        <div class="panel-head">
          <h4><i class="fas fa-newspaper"></i> 新闻公告</h4>
          <button class="link-more" @click="navigateTo('announcements')">更多 <i class="fas fa-angle-right"></i></button>
        </div>
        <div class="panel-body">
          <div v-if="newsLoading" class="loading-row">加载中...</div>
          <div v-else-if="newsList.length === 0" class="empty"><span>暂无新闻公告</span></div>
          <div v-for="n in newsList" :key="n.guid" class="news-row">
            <span class="news-dot" :class="{ hot: isRecent(n.publicTime) }"></span>
            <div class="news-main">
              <span class="news-title">
                <em v-if="isRecent(n.publicTime)" class="badge-new">新</em>{{ n.titles || n.Titles }}
              </span>
              <span class="news-date">{{ fmtDate(n.publicTime || n.PublicTime) }}</span>
            </div>
          </div>
        </div>
      </div>
      <!-- 我的申请 -->
      <div class="panel myapp-panel">
        <div class="panel-head">
          <h4><i class="fas fa-pen-square"></i> 我的申请</h4>
          <button class="link-more" @click="navigateTo('submittedTasks')">更多 <i class="fas fa-angle-right"></i></button>
        </div>
        <div class="panel-body">
          <div v-if="appsLoading" class="loading-row">加载中...</div>
          <div v-else-if="myApplications.length === 0" class="empty"><span>暂无申请</span></div>
          <div v-for="a in myApplications" :key="a.taskId || a.TaskId" class="myapp-row">
            <span class="app-status" :class="getAppStatus(a)">{{ getAppStatusLabel(a) }}</span>
            <span class="app-title">{{ a.processName || a.ProcessName }}</span>
            <span class="app-date">{{ fmtDate(a.createAt || a.CreateAt) }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- ===== 第三行：待办事项 + 日历日程（上下） ===== -->
    <div class="two-col-row">
      <!-- 左：待办事项 -->
      <div class="panel todo-panel">
        <div class="panel-head">
          <h4><i class="fas fa-tasks"></i> 待办事项</h4>
          <span class="hint">共 {{ todoCount }} 项，紧急 {{ urgentCount }} 项</span>
        </div>
        <div class="panel-body panel-scroll">
          <div v-if="todos.length === 0" class="empty">
            <i class="fas fa-check-circle"></i>
            <span>暂无待办事项</span>
          </div>
          <div v-for="t in todos" :key="t.id" class="todo-row" @click="openTask(t)" style="cursor: pointer;">
            <div class="todo-bar" :class="t.priority"></div>
            <div class="todo-main">
              <!-- 第一行：任务号+流程名 同一列 | 流水号 | 摘要信息 -->
              <div class="todo-line1">
                <div class="todo-col col-task-node">
                  <div class="badge-row">
                    <span class="badge badge-task">任务号 {{ t.taskNo || '--' }}</span>
                    <span class="badge badge-process">{{ t.title || '--' }}</span>
                  </div>
                </div>
                <div class="todo-col col-serial">
                  <span class="tv serial-no" v-if="t.flowNo">#{{ t.flowNo }}</span>
                  <span class="tv no-data" v-else>--</span>
                </div>
                <div class="todo-col col-desc">
                  <span class="tv desc-text">{{ t.summary || '暂无摘要' }}</span>
                </div>
              </div>
              <!-- 第二行：当前节点 · 发起人 · 到达时间 -->
              <div class="todo-line2">
                <span class="badge badge-node">{{ t.nodeName || '当前节点' }}</span>
                <span class="sep">·</span>
                <span class="tv meta-text"><i class="fas fa-user-circle"></i> {{ t.applicant || '--' }}</span>
                <span class="sep">·</span>
                <span class="tv meta-text"><i class="far fa-clock"></i> {{ t.time || '--' }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 右：日历日程 + 当日安排（上下） -->
      <div class="col-right-stack">
        <!-- 日历 -->
        <div class="panel calendar-wrap">
          <div class="panel-head cal-head">
            <h4><i class="fas fa-calendar-alt"></i> 日历日程</h4>
          </div>
          <div class="cal-nav-month">
            <button @click="prevMonth"><i class="fas fa-chevron-left"></i></button>
            <span>{{ calYear }}年{{ calMonth + 1 }}月</span>
            <button @click="nextMonth"><i class="fas fa-chevron-right"></i></button>
          </div>
          <div class="panel-body cal-body">
            <div class="cal-weekdays">
              <span v-for="w in weekLabels" :key="w">{{ w }}</span>
            </div>
            <div class="cal-grid">
              <div v-for="day in calDays" :key="day.key" class="cal-cell"
                :class="{ other: !day.cur, today: day.today, active: day.dateStr === selectedDate }"
                @click="pickDate(day)">
                <span class="cal-date">{{ day.num }}</span>
                <span v-if="day.dots.length" class="cal-dots">
                  <i v-for="(d, i) in day.dots.slice(0, 2)" :key="i" class="cal-dot" :style="{ background: d }"></i>
                  <em v-if="day.dots.length > 2">+</em>
                </span>
              </div>
            </div>
            <div class="cal-today-row">
              <button class="cal-today" @click="goToday">返回今天</button>
            </div>
          </div>
        </div>

        <!-- 当日安排 -->
        <div class="panel today-schedule">
          <div class="panel-head">
            <h4><i class="fas fa-list-ul"></i> 当日安排</h4>
            <button class="btn-add" @click="openAddModal">+ 添加</button>
          </div>
          <div class="panel-body">
            <div class="sched-date-label">{{ selectedDateLabel }}</div>
            <div v-if="schedLoading" class="loading-row">加载中...</div>
            <div v-else-if="selectedSchedules.length === 0" class="empty sched-empty">
              <span>暂无日程安排</span>
            </div>
            <div v-for="s in selectedSchedules" :key="s.guid || s.Guid" class="sched-row">
              <span class="sched-dot" :style="{ background: s.color || s.Color || '#4a90d9' }"></span>
              <div class="sched-main">
                <div class="sched-title">{{ s.title || s.Title }}</div>
                <div class="sched-sub">
                  <span v-if="s.startTime || s.StartTime"><i class="far fa-clock"></i> {{ s.startTime || s.StartTime }}</span>
                  <span v-if="s.location || s.Location"><i class="fas fa-map-marker-alt"></i> {{ s.location || s.Location }}</span>
                </div>
              </div>
              <button class="sched-del" @click="delSchedule(s.guid || s.Guid)" title="删除"><i class="fas fa-trash-alt"></i></button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ===== 添加日程弹窗 ===== -->
    <Teleport to="body">
      <div v-if="showModal" class="modal-overlay" @click.self="showModal = false">
        <div class="modal-box">
          <div class="modal-head">
            <h4><i class="fas fa-calendar-plus"></i> 添加日程</h4>
            <button @click="showModal = false" class="modal-x">&times;</button>
          </div>
          <div class="modal-form">
            <label>日期</label><input type="date" v-model="form.date" />
            <label>时间</label><input type="time" v-model="form.time" />
            <label>标题 <em>*</em></label><input type="text" v-model="form.title" placeholder="日程标题" />
            <label>地点</label><input type="text" v-model="form.location" placeholder="选填" />
            <label>颜色</label>
            <div class="color-row">
              <span v-for="c in colors" :key="c" class="cc" :class="{ sel: form.color === c }" :style="{ background: c }" @click="form.color = c"></span>
            </div>
          </div>
          <div class="modal-foot">
            <button class="btn-cancel" @click="showModal = false">取消</button>
            <button class="btn-ok" @click="saveSchedule">确认添加</button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { getTodoTask, getAlreadyInitiated, buildPagedRequest } from '../api/task'
import { getAnnouncementList } from '../api/announcement'
import { getMyQuickLinks } from '../api/quicklink'
import { getDashboardSchedules, saveSchedule as apiSaveSchedule, deleteSchedule as apiDeleteSchedule } from '../api/schedule'
const emit = defineEmits(['navigate'])

// ==================== 导航 ====================
function navigateTo(path, shortcut) {
  // 流程类型快捷入口：存储 autoLaunch id，跳转到可发起页面后自动弹出
  if (shortcut?.para0 === 'process' && shortcut?.para1) {
    sessionStorage.setItem('autoLaunchProcessId', shortcut.para1)
  }
  emit('navigate', path)
}

// ==================== 快捷入口 ====================
const shortcutFolded = ref(true)
const shortcuts = ref([
  { guid: '_def_launch', title: '可发起', icon: 'fas fa-play-circle', path: 'launchableProcesses', isDefault: 1 },
  { guid: '_def_todo', title: '待处理', icon: 'fas fa-clipboard-list', path: 'pendingTasks', isDefault: 1 },
  { guid: '_def_submitted', title: '已发起', icon: 'fas fa-check-double', path: 'submittedTasks', isDefault: 1 }
])

// 模块名称 → pageKey 映射（对应 Sidebar 导航）
const modulePageMap = {
  '用户管理': 'users',
  '角色管理': 'roles',
  '组织管理': 'organizations',
  '权限管理': 'permissions',
  '操作日志': 'logs',
  '公告管理': 'announcements',
  '数据库管理': 'databaseManager',
  '语句执行': 'sqlExecution',
  '日志查询': 'logQuery',
  '流程管理': 'processManagement',
  '定时作业管理': 'jobManagement',
  '任务管理': 'taskManagement',
  '模块管理': 'moduleManagement',
  '系统管理': 'systemManagement',
  '公司管理': 'companyManagement',
  '租户管理': 'tenantManagement',
  '系统报表': 'systemReports',
  '自定义报表': 'customReports',
  '报表管理': 'reportManagement',
  '公告权限': 'aaPermissions',
  '模块权限': 'baPermissions',
  '流程权限': 'laPermissions',
  '报表权限': 'daPermissions',
  '个人信息': 'personalInfo',
  '密码修改': 'passwordChange',
  '代理设置': 'proxySettings',
  '快捷入口': 'quickLinkManager',
  '待处理': 'pendingTasks',
  '可发起': 'launchableProcesses',
  '草稿箱': 'drafts',
  '已处理任务': 'completedTasks',
  '已提交任务': 'submittedTasks',
  '可查看任务': 'viewableTasks',
  '抄送任务': 'ccTasks',
  '表单设计': 'formDesigner',
  '经典表单设计': 'oldFormDesigner',
  '流程设计': 'processDesigner'
}

async function loadShortcuts() {
  try {
    const res = await getMyQuickLinks()
    // http 拦截器已通过 normalizeKeys 转为 camelCase
    const data = res.data || []
    if (data.length > 0) {
      const merged = [
        ...shortcuts.value.filter(s => s.isDefault),
        ...data
          .filter(d => !shortcuts.value.find(x => x.guid === d.guid))
          .map(d => {
            // 根据类型解析导航路径
            let path = d.path || ''
            if (!path) {
              if (d.para0 === 'module') {
                // 模块名匹配 pageKey，匹配不到兜底到模块管理
                path = modulePageMap[d.title] || 'moduleManagement'
              } else if (d.para0 === 'process') {
                path = 'launchableProcesses'
              }
            }
            return {
              guid: d.guid,
              title: d.title || '未命名',
              icon: d.icon || 'fas fa-link',
              path: path,
              para0: d.para0,
              para1: d.para1,
              isDefault: d.isDefault
            }
          })
      ]
      shortcuts.value = merged
    }
  } catch (e) {
    console.error('加载快捷入口失败', e)
  }
}

// ==================== 待办事项 ====================
const todos = ref([])

function priorityClass(p) {
  const s = (p ?? '').toString()
  if (s === '高' || s === '紧急') return 'urgent'
  if (s === '中') return 'high'
  if (s === '低') return 'normal'
  const n = Number(p)
  return n === 2 ? 'urgent' : n === 1 ? 'high' : 'normal'
}

function mapTodo(t) {
  return {
    id: t.taskId ?? t.TaskId,
    taskNo: String(t.taskId ?? t.TaskId ?? ''),
    flowNo: t.serialNum || t.SerialNum || '',
    title: t.processName || t.ProcessName || '',
    nodeName: t.nodeName || t.NodeName || '',
    summary: t.description || t.Description || t.para0 || t.Para0 || '',
    type: 'approval', typeLabel: '审批',
    applicant: t.initiatorName || t.InitiatorName || t.initiator || t.Initiator || '',
    time: (t.createAt || t.CreateAt || '').toString().replace('T', ' ').substring(0, 16),
    priority: priorityClass(t.priority ?? t.Priority),
    _raw: t
  }
}

async function loadTodos() {
  try {
    const res = await getTodoTask(buildPagedRequest({ pageNumber: 0, pageSize: 50 }))
    const rawData = res.rows || res.Rows || []
    console.log('待办原始数据:', rawData)
    todos.value = rawData.map(mapTodo)
    console.log('待办映射后:', todos.value)
  } catch (e) {
    console.error('加载待办失败', e)
  }
}

function openTask(task) {
  window.dispatchEvent(new CustomEvent('navigateToTaskView', {
    detail: {
      taskId: task.taskNo || task.id,
      mode: 'todo',
      processName: task.title || '',
      title: `任务-${task.title || task.taskNo}`,
      taskData: task._raw || task
    }
  }))
}

const todoCount = computed(() => todos.value.length)
const urgentCount = computed(() => todos.value.filter(t => t.priority === 'urgent').length)

// ==================== 新闻公告 + 系统公告 ====================
const newsLoading = ref(false)
const newsList = ref([])
const sysNotices = ref([])

function isRecent(timeStr) {
  if (!timeStr) return false
  const d = new Date(timeStr)
  const now = new Date()
  return (now - d) < 7 * 24 * 60 * 60 * 1000
}

function fmtDate(timeStr) {
  if (!timeStr) return ''
  const s = String(timeStr).replace('T', ' ').substring(0, 10)
  return s
}

function getNoticeLevel(item) {
  const types = (item.types || item.Types || '').toLowerCase()
  if (types.includes('维护') || types.includes('升级')) return 'warn'
  if (types.includes('重要') || types.includes('紧急')) return 'danger'
  return 'info'
}

function getNoticeLabel(item) {
  const types = (item.types || item.Types || '')
  if (types.includes('维护')) return '维护'
  if (types.includes('重要')) return '重要'
  return '通知'
}

async function loadAnnouncements() {
  newsLoading.value = true
  try {
    const res = await getAnnouncementList('')
    const all = (res.Data || res.data || [])
    const published = all.filter(a => (a.publicTime || a.PublicTime))
    published.sort((a, b) => new Date(b.publicTime || b.PublicTime) - new Date(a.publicTime || a.PublicTime))
    newsList.value = published.slice(0, 5)
    sysNotices.value = published.filter(a => {
      const t = (a.types || a.Types || '')
      return t.includes('系统') || t.includes('公告') || t.includes('维护') || t.includes('重要')
    }).slice(0, 5)
  } catch (e) {
    console.error('加载公告失败', e)
  }
  newsLoading.value = false
}

// ==================== 我的申请 ====================
const appsLoading = ref(false)
const myApplications = ref([])

function getAppStatus(a) {
  const s = (a.status || a.Status || '').toString()
  if (s === '已完成' || s === '已通过') return 'done'
  if (s === '处理中' || s === '审批中') return 'pending'
  if (s === '已退回' || s === '已驳回') return 'reject'
  return 'pending'
}

function getAppStatusLabel(a) {
  const s = (a.status || a.Status || '').toString()
  if (s) return s.substring(0, 3)
  return '处理中'
}

async function loadMyApps() {
  appsLoading.value = true
  try {
    const res = await getAlreadyInitiated(buildPagedRequest({ pageNumber: 0, pageSize: 5, sortName: 'CreateAt', sortOrder: 'desc' }))
    myApplications.value = (res.rows || res.Rows || [])
  } catch (e) {
    console.error('加载我的申请失败', e)
  }
  appsLoading.value = false
}

const myAppsTotal = computed(() => myApplications.value.length)

// ==================== 日历 ====================
const calYear = ref(new Date().getFullYear())
const calMonth = ref(new Date().getMonth())
const selectedDate = ref(todayDateStr())
const showModal = ref(false)
const schedLoading = ref(false)
const colors = ['#4a90d9', '#52c41a', '#fa8c16', '#f5222d', '#9333ea', '#13c2c2', '#eb2f96']
const weekLabels = ['一', '二', '三', '四', '五', '六', '日']

function todayDateStr() { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` }
function pad(n) { return String(n).padStart(2, '0') }
const todayShort = computed(() => { const d = new Date(); const w = ['日', '一', '二', '三', '四', '五', '六']; return `${d.getMonth() + 1}/${d.getDate()} 周${w[d.getDay()]}` })

const allSchedules = ref([])

const calDays = computed(() => {
  const y = calYear.value, m = calMonth.value, today = todayDateStr()
  const first = new Date(y, m, 1); let dow = first.getDay(); dow = dow === 0 ? 6 : dow - 1
  const total = new Date(y, m + 1, 0).getDate(), prevTotal = new Date(y, m, 0).getDate()
  const days = []
  for (let i = dow - 1; i >= 0; i--) { const d = prevTotal - i; days.push(buildDay(d, `${y}-${pad(m === 0 ? 12 : m)}-${pad(d)}`, false, today)) }
  for (let d = 1; d <= total; d++) { days.push(buildDay(d, `${y}-${pad(m + 1)}-${pad(d)}`, true, today)) }
  const rem = 42 - days.length
  for (let d = 1; d <= rem; d++) { const nm = m + 2 > 12 ? m + 2 - 12 : m + 2, ny = m + 2 > 12 ? y + 1 : y; days.push(buildDay(d, `${ny}-${pad(nm)}-${pad(d)}`, false, today)) }
  return days
})

function buildDay(num, ds, cur, today) {
  const dots = allSchedules.value.filter(s => (s.scheduleDate || s.ScheduleDate || '').toString().substring(0, 10) === ds).map(s => s.color || s.Color || '#4a90d9')
  return { key: ds, num, dateStr: ds, cur, today: ds === today, dots }
}

const selectedDateLabel = computed(() => {
  const s = selectedSchedules.value
  const d = new Date(selectedDate.value + 'T00:00:00'); const w = ['日', '一', '二', '三', '四', '五', '六']
  return `${d.getMonth() + 1}月${d.getDate()}日 周${w[d.getDay()]} · ${s.length}项`
})

const selectedSchedules = computed(() =>
  allSchedules.value.filter(s => (s.scheduleDate || s.ScheduleDate || '').toString().substring(0, 10) === selectedDate.value)
)

const todayScheduleCount = computed(() =>
  allSchedules.value.filter(s => (s.scheduleDate || s.ScheduleDate || '').toString().substring(0, 10) === todayDateStr()).length
)

function prevMonth() { if (calMonth.value === 0) { calMonth.value = 11; calYear.value-- } else calMonth.value-- }
function nextMonth() { if (calMonth.value === 11) { calMonth.value = 0; calYear.value++ } else calMonth.value++ }
function goToday() { const d = new Date(); calYear.value = d.getFullYear(); calMonth.value = d.getMonth(); selectedDate.value = todayDateStr() }
function pickDate(day) { if (day.dateStr) selectedDate.value = day.dateStr }
function openAddModal() { Object.assign(form, { date: selectedDate.value, time: '', title: '', location: '', color: '#4a90d9' }); showModal.value = true }

async function saveSchedule() {
  if (!form.title.trim()) { $message.warning('请输入日程标题'); return }
  try {
    await apiSaveSchedule({
      Guid: '', ScheduleDate: form.date + 'T00:00:00', Title: form.title,
      StartTime: form.time || '', Location: form.location, Color: form.color,
      Content: ''
    })
    await loadSchedules()
    const d = new Date(form.date + 'T00:00:00')
    calYear.value = d.getFullYear(); calMonth.value = d.getMonth()
    selectedDate.value = form.date
    showModal.value = false
  } catch (e) {
    console.error('保存日程失败', e)
    $message.error('保存失败: ' + (e.message || '未知错误'))
  }
}

async function delSchedule(guid) {
  if (!guid) return
  try {
    await apiDeleteSchedule(guid)
    await loadSchedules()
  } catch (e) {
    console.error('删除日程失败', e)
  }
}

async function loadSchedules() {
  schedLoading.value = true
  try {
    const res = await getDashboardSchedules()
    allSchedules.value = (res.Data || res.data || [])
  } catch (e) {
    console.error('加载日程失败', e)
  }
  schedLoading.value = false
}

const form = reactive({ date: todayDateStr(), time: '', title: '', location: '', color: '#4a90d9' })

// ==================== 初始化 ====================
onMounted(() => {
  loadTodos()
  loadAnnouncements()
  loadMyApps()
  loadShortcuts()
  loadSchedules()
})
</script>

<style scoped>
.oa-dashboard { padding: 16px 20px; background: #f0f2f5; min-height: calc(100vh - 120px); font-size: 13px; }

/* ========== 统计卡片 ========== */
.stats-row { display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px; margin-bottom: 14px; }
.stat-card { background: #fff; border-radius: 12px; padding: 20px 24px; display: flex; align-items: center; gap: 16px; position: relative; box-shadow: 0 2px 8px rgba(0, 0, 0, .04); transition: all .25s; overflow: hidden; }
.stat-card::after { content: ''; position: absolute; right: -20px; top: -20px; width: 80px; height: 80px; border-radius: 50%; opacity: .06; }
.stat-card.bg-blue::after { background: #4a90d9; }
.stat-card.bg-green::after { background: #52c41a; }
.stat-card.bg-orange::after { background: #fa8c16; }
.stat-card:hover { box-shadow: 0 6px 20px rgba(0, 0, 0, .08); transform: translateY(-2px); }
.stat-left { flex-shrink: 0; }
.stat-icon { width: 50px; height: 50px; border-radius: 14px; display: flex; align-items: center; justify-content: center; font-size: 22px; color: #fff; box-shadow: 0 4px 12px rgba(0,0,0,.1); }
.stat-card.bg-blue .stat-icon { background: linear-gradient(135deg, #5b8def, #6e4bb8); }
.stat-card.bg-green .stat-icon { background: linear-gradient(135deg, #11998e, #38ef7d); }
.stat-card.bg-orange .stat-icon { background: linear-gradient(135deg, #fa8231, #f7b731); }
.stat-num { font-size: 30px; font-weight: 800; color: #1a1a1a; line-height: 1.1; letter-spacing: -1px; }
.stat-title { font-size: 13px; color: #999; margin-top: 3px; font-weight: 500; }
.stat-badge { position: absolute; top: 14px; right: 20px; font-size: 11px; color: #bbb; background: #fafafa; padding: 2px 8px; border-radius: 10px; }
.stat-badge.urgent { color: #fff; background: #ff4d4f; font-weight: 600; }
.stat-badge.process { color: #fa8c16; background: #fff7e6; font-weight: 500; }
/* 快捷入口卡片（替换统计卡第三位） */
.shortcuts-card { padding: 12px 16px; display: block; }
.shortcut-card-body { display: flex; align-items: center; gap: 0; }
.shortcut-card-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 2px; flex: 1; }
.shortcut-card-grid .sc-item { padding: 6px 3px 4px; gap: 3px; }
.shortcut-card-grid .sc-icon { width: 28px; height: 28px; border-radius: 7px; font-size: 14px; }
.shortcut-card-grid .sc-item span { font-size: 10px; }
.sc-expand-btn { flex-shrink: 0; margin-left: 8px; background: #f0f5ff; border: 1px solid #d6e4ff; color: #4a90d9; border-radius: 6px; padding: 5px 10px; font-size: 11px; cursor: pointer; display: flex; align-items: center; gap: 4px; transition: all .2s; white-space: nowrap; }
.sc-expand-btn:hover { background: #4a90d9; color: #fff; border-color: #4a90d9; }

/* ========== 通用面板 ========== */
.panel { background: #fff; border-radius: 12px; box-shadow: 0 2px 8px rgba(0, 0, 0, .04); transition: box-shadow .2s; }
.panel:hover { box-shadow: 0 4px 16px rgba(0, 0, 0, .06); }
.panel-head { padding: 12px 16px; border-bottom: 1px solid #f5f5f5; display: flex; align-items: center; justify-content: space-between; }
.panel-head h4 { margin: 0; font-size: 14px; font-weight: 700; color: #333; display: flex; align-items: center; gap: 8px; }
.panel-head h4 i { color: #4a90d9; font-size: 14px; opacity: .8; }
.panel-body { padding: 10px 16px; }
.panel-scroll { max-height: 580px; overflow-y: auto; }
.panel-scroll::-webkit-scrollbar { width: 4px; }
.panel-scroll::-webkit-scrollbar-thumb { background: #d9d9d9; border-radius: 2px; }
.panel-scroll::-webkit-scrollbar-thumb:hover { background: #bbb; }
.empty { text-align: center; padding: 24px 0; color: #d9d9d9; display: flex; flex-direction: column; align-items: center; gap: 6px; }
.empty i { font-size: 36px; opacity: .5; }
.empty span { font-size: 12px; }
.hint { font-size: 11px; color: #bbb; background: #fafafa; padding: 2px 10px; border-radius: 10px; }
.link-more { background: none; border: none; font-size: 12px; color: #bbb; cursor: pointer; display: flex; align-items: center; gap: 3px; transition: color .2s; }
.link-more:hover { color: #4a90d9; }
.toggle-btn { font-size: 15px; width: 28px; height: 28px; border-radius: 6px; display: flex; align-items: center; justify-content: center; }
.toggle-btn:hover { background: #f0f5ff; color: #4a90d9; }
.btn-add { font-size: 11px; background: linear-gradient(135deg, #4a90d9, #5b8def); color: #fff; border: none; border-radius: 6px; padding: 5px 12px; cursor: pointer; font-weight: 500; transition: all .2s; }
.btn-add:hover { background: linear-gradient(135deg, #3a7bc8, #4a7de0); box-shadow: 0 2px 8px rgba(74,144,217,.3); }
.loading-row { text-align: center; padding: 24px 0; color: #bbb; font-size: 12px; }

/* ========== 快捷入口 ========== */
.shortcuts-panel { margin-bottom: 14px; }
.shortcuts-panel .panel-body { padding: 8px 4px 4px; overflow: hidden; }
.shortcut-grid { display: grid; grid-template-columns: repeat(6, 1fr); gap: 4px; padding: 4px 0; }
.sc-item { display: flex; flex-direction: column; align-items: center; gap: 6px; padding: 12px 4px 10px; border-radius: 8px; cursor: pointer; transition: all .2s; }
.sc-item:hover { background: #f0f5ff; }
.sc-item:active { transform: scale(.95); }
.sc-icon { width: 42px; height: 42px; border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 20px; background: linear-gradient(135deg, #f0f5ff, #e8f4fd); color: #4a90d9; transition: all .2s; }
.sc-item:hover .sc-icon { background: linear-gradient(135deg, #dbeafe, #cfe1f5); box-shadow: 0 4px 12px rgba(74,144,217,.15); }
.sc-item span { font-size: 12px; color: #666; white-space: nowrap; font-weight: 500; }

/* 展开弹窗 */
.shortcut-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,.35); z-index: 9999; display: flex; align-items: center; justify-content: center; }
.shortcut-popup { background: #fff; border-radius: 14px; box-shadow: 0 12px 40px rgba(0,0,0,.15); width: 640px; max-width: 90vw; max-height: 75vh; display: flex; flex-direction: column; overflow: hidden; }
.popup-head { display: flex; align-items: center; justify-content: space-between; padding: 16px 20px; border-bottom: 1px solid #f0f0f0; }
.popup-head h4 { font-size: 15px; color: #333; display: flex; align-items: center; gap: 8px; }
.popup-head h4 i { color: #4a90d9; }
.popup-head button { background: none; border: none; color: #999; cursor: pointer; font-size: 16px; width: 30px; height: 30px; border-radius: 6px; display: flex; align-items: center; justify-content: center; transition: all .15s; }
.popup-head button:hover { background: #f5f5f5; color: #ff4d4f; }
.popup-body { padding: 20px; overflow-y: auto; flex: 1; }
.popup-body::-webkit-scrollbar { width: 4px; }
.popup-body::-webkit-scrollbar-thumb { background: #d9d9d9; border-radius: 2px; }
.shortcut-grid-expanded { display: grid; grid-template-columns: repeat(6, 1fr); gap: 8px; }
.shortcut-grid-expanded .sc-item { padding: 16px 6px 12px; }
.shortcut-grid-expanded .sc-icon { width: 48px; height: 48px; border-radius: 12px; font-size: 22px; }
.shortcut-grid-expanded .sc-item span { font-size: 12px; }

/* ========== 三列行 ========== */
.three-col-row { display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px; margin-bottom: 14px; }

/* ========== 两列行 ========== */
.two-col-row { display: grid; grid-template-columns: 1fr 310px; gap: 14px; align-items: stretch; }
.col-right-stack { display: flex; flex-direction: column; gap: 10px; }
.col-right-stack .panel { display: flex; flex-direction: column; }
.col-right-stack .panel-body { flex: 1; overflow: hidden; }
.today-schedule { flex: 1; }
.calendar-wrap { flex-shrink: 0; }

/* ========== 新闻公告 ========== */
.news-row { display: flex; gap: 8px; padding: 9px 0; border-bottom: 1px solid #fafafa; align-items: flex-start; transition: background .15s; }
.news-row:hover { background: #fafbfc; }
.news-row:last-child { border-bottom: none; }
.news-dot { width: 5px; height: 5px; border-radius: 50%; background: #ddd; margin-top: 5px; flex-shrink: 0; }
.news-dot.hot { background: #ff4d4f; box-shadow: 0 0 0 4px rgba(255,77,79,.12); }
.news-main { flex: 1; display: flex; justify-content: space-between; align-items: flex-start; min-width: 0; }
.news-title { font-size: 12px; color: #555; display: flex; align-items: center; gap: 5px; flex: 1; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.badge-new { font-size: 10px; background: #ff4d4f; color: #fff; padding: 1px 5px; border-radius: 3px; font-style: normal; font-weight: 600; letter-spacing: .5px; }
.news-date { font-size: 10px; color: #ccc; flex-shrink: 0; margin-left: 8px; }

/* ========== 系统公告 ========== */
.sys-row { display: flex; align-items: center; gap: 8px; padding: 9px 0; border-bottom: 1px solid #fafafa; transition: background .15s; }
.sys-row:hover { background: #fafbfc; }
.sys-row:last-child { border-bottom: none; }
.sys-tag { font-size: 10px; padding: 2px 6px; border-radius: 3px; font-weight: 600; flex-shrink: 0; letter-spacing: .5px; }
.sys-tag.warn { background: #fff7e6; color: #d46b08; }
.sys-tag.info { background: #e6f7ff; color: #1890ff; }
.sys-tag.danger { background: #fff2f0; color: #ff4d4f; }
.sys-title { font-size: 12px; color: #555; flex: 1; overflow: hidden; white-space: nowrap; text-overflow: ellipsis; }
.sys-date { font-size: 10px; color: #ccc; flex-shrink: 0; }

/* ========== 我的申请 ========== */
.myapp-row { display: flex; align-items: center; gap: 8px; padding: 9px 0; border-bottom: 1px solid #fafafa; transition: background .15s; }
.myapp-row:hover { background: #fafbfc; }
.myapp-row:last-child { border-bottom: none; }
.app-status { font-size: 10px; padding: 2px 6px; border-radius: 3px; font-weight: 600; flex-shrink: 0; letter-spacing: .5px; }
.app-status.done { background: #f6ffed; color: #52c41a; }
.app-status.pending { background: #fff7e6; color: #d46b08; }
.app-status.reject { background: #fff2f0; color: #ff4d4f; }
.app-title { font-size: 12px; color: #555; flex: 1; overflow: hidden; white-space: nowrap; text-overflow: ellipsis; }
.app-date { font-size: 10px; color: #ccc; flex-shrink: 0; }

/* ========== 待办 ========== */
.todo-row { display: flex; align-items: stretch; padding: 12px 0; border-bottom: 1px solid #f0f0f0; transition: background .15s; }
.todo-row:hover { background: #f6f9fe; }
.todo-row:last-child { border-bottom: none; }
.todo-bar { width: 3px; border-radius: 2px; flex-shrink: 0; margin-right: 12px; align-self: stretch; }
.todo-bar.urgent { background: #ff4d4f; }
.todo-bar.high { background: #fa8c16; }
.todo-bar.normal { background: #e0e0e0; }
.todo-main { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 4px; }
/* 第一行：3列 */
.todo-line1 { display: flex; gap: 8px; align-items: flex-start; }
.col-task-node { flex: 0 0 30%; min-width: 140px; display: flex; align-items: center; }
.col-serial { flex: 0 0 30%; min-width: 90px; display: flex; align-items: center; padding-top: 1px; }
.col-desc { flex: 0 0 40%; min-width: 120px; display: flex; align-items: flex-start; }
/* 第一行具体值 */
.badge-row { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.badge { display: inline-block; padding: 3px 10px; border-radius: 4px; font-size: 12px; font-weight: 600; white-space: nowrap; }
.badge-task { background: #e8f0fe; color: #1967d2; border: 1px solid #a8c7fa; }
.badge-process { background: #fff3e0; color: #e65100; border: 1px solid #ffcc80; }
.badge-node { background: #e8f5e9; color: #2e7d32; border: 1px solid #a5d6a7; }
.tv { color: #333; }
.serial-no { font-family: 'Consolas','Courier New',monospace; font-size: 14px; color: #1a73e8; font-weight: 700; white-space: nowrap; }
.desc-text { font-size: 13px; color: #555; line-height: 1.55; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; word-break: break-all; }
/* 第二行：当前节点 · 发起人 · 到达时间 */
.todo-line2 { display: flex; align-items: center; gap: 0; flex-wrap: wrap; }
.todo-line2 .tv { font-size: 12px; color: #777; white-space: nowrap; }
.meta-text { display: inline-flex; align-items: center; gap: 3px; }
.meta-text i { font-size: 11px; color: #bbb; }
.sep { margin: 0 6px; color: #d9d9d9; }
.no-data { color: #d9d9d9; font-style: italic; font-size: 12px; }

/* ========== 日历 ========== */
.cal-head { padding-bottom: 4px; }
.cal-nav-month { display: flex; align-items: center; justify-content: space-between; padding: 0 4px 8px; }
.cal-nav-month button { background: #fafafa; border: none; color: #999; cursor: pointer; font-size: 12px; width: 26px; height: 26px; border-radius: 6px; display: flex; align-items: center; justify-content: center; transition: all .15s; }
.cal-nav-month button:hover { background: #f0f5ff; color: #4a90d9; }
.cal-nav-month span { font-size: 13px; font-weight: 700; color: #333; }
.cal-body { padding: 0 8px 8px !important; }
.cal-weekdays { display: grid; grid-template-columns: repeat(7, 1fr); text-align: center; font-size: 11px; color: #bbb; padding-bottom: 4px; font-weight: 500; }
.cal-grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 2px; }
.cal-cell { text-align: center; padding: 3px 0; border-radius: 6px; cursor: pointer; font-size: 12px; position: relative; transition: .15s; min-height: 32px; display: flex; flex-direction: column; align-items: center; justify-content: center; }
.cal-cell:hover { background: #f0f5ff; }
.cal-cell.other { color: #ddd; }
.cal-cell.other:hover { background: #fafafa; }
.cal-cell.today { background: #e6f7ff; }
.cal-cell.today .cal-date { background: #4a90d9; color: #fff; border-radius: 50%; width: 24px; height: 24px; display: inline-flex; align-items: center; justify-content: center; font-weight: 700; font-size: 12px; }
.cal-cell.active { background: #f0f5ff; box-shadow: inset 0 0 0 1px #4a90d9; }
.cal-dots { display: flex; gap: 2px; justify-content: center; margin-top: 2px; }
.cal-dot { width: 5px; height: 5px; border-radius: 50%; display: inline-block; }
.cal-dots em { font-size: 9px; color: #bbb; font-style: normal; }
.cal-today-row { text-align: center; margin-top: 6px; }
.cal-today { background: #4a90d9; color: #fff; border: none; border-radius: 6px; padding: 4px 14px; font-size: 11px; cursor: pointer; font-weight: 500; transition: all .15s; }
.cal-today:hover { background: #3a7bc8; box-shadow: 0 2px 8px rgba(74,144,217,.3); }

/* ========== 当日安排 ========== */
.sched-date-label { font-size: 12px; color: #4a90d9; margin-bottom: 4px; padding: 4px 8px; background: #f0f5ff; border-radius: 6px; font-weight: 500; }
.sched-row { display: flex; align-items: center; gap: 10px; padding: 8px 0; border-bottom: 1px solid #fafafa; transition: background .15s; }
.sched-row:hover { background: #fafbfc; }
.sched-row:last-child { border-bottom: none; }
.sched-dot { width: 8px; height: 8px; border-radius: 2px; flex-shrink: 0; }
.sched-main { flex: 1; min-width: 0; }
.sched-title { font-size: 13px; color: #333; font-weight: 600; }
.sched-sub { font-size: 11px; color: #bbb; margin-top: 3px; display: flex; gap: 12px; }
.sched-sub span { display: flex; align-items: center; gap: 3px; }
.sched-del { background: none; border: none; color: #ddd; cursor: pointer; font-size: 13px; padding: 4px; border-radius: 4px; transition: all .15s; }
.sched-del:hover { color: #ff4d4f; background: #fff2f0; }
.sched-empty { padding: 20px 0; }

/* ========== 弹窗 ========== */
.modal-overlay { position: fixed; inset: 0; background: rgba(0, 0, 0, .35); display: flex; align-items: center; justify-content: center; z-index: 999; backdrop-filter: blur(2px); }
.modal-box { background: #fff; border-radius: 14px; width: 400px; box-shadow: 0 16px 48px rgba(0, 0, 0, .15); animation: slideUp .2s ease; }
@keyframes slideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
.modal-head { padding: 14px 20px; border-bottom: 1px solid #f5f5f5; display: flex; align-items: center; justify-content: space-between; }
.modal-head h4 { margin: 0; font-size: 14px; font-weight: 700; color: #333; }
.modal-x { background: none; border: none; font-size: 22px; color: #bbb; cursor: pointer; width: 30px; height: 30px; border-radius: 6px; display: flex; align-items: center; justify-content: center; transition: all .15s; }
.modal-x:hover { background: #fff2f0; color: #ff4d4f; }
.modal-form { padding: 16px 20px; display: flex; flex-direction: column; gap: 8px; }
.modal-form label { font-size: 12px; color: #888; font-weight: 600; }
.modal-form label em { color: #ff4d4f; font-style: normal; }
.modal-form input[type="text"],
.modal-form input[type="date"],
.modal-form input[type="time"] { border: 1px solid #e8e8e8; border-radius: 8px; padding: 8px 12px; font-size: 13px; transition: border-color .2s; }
.modal-form input:focus { border-color: #4a90d9; outline: none; box-shadow: 0 0 0 3px rgba(74,144,217,.1); }
.color-row { display: flex; gap: 10px; }
.cc { width: 28px; height: 28px; border-radius: 50%; cursor: pointer; border: 2px solid transparent; transition: all .15s; }
.cc:hover { transform: scale(1.15); }
.cc.sel { border-color: #333; box-shadow: 0 0 0 2px #fff, 0 0 0 4px currentColor; }
.modal-foot { padding: 12px 20px; border-top: 1px solid #f5f5f5; display: flex; justify-content: flex-end; gap: 10px; }
.btn-cancel { background: #f5f5f5; border: none; border-radius: 8px; padding: 8px 18px; font-size: 12px; cursor: pointer; font-weight: 500; transition: all .15s; }
.btn-cancel:hover { background: #e8e8e8; }
.btn-ok { background: linear-gradient(135deg, #4a90d9, #5b8def); color: #fff; border: none; border-radius: 8px; padding: 8px 18px; font-size: 12px; cursor: pointer; font-weight: 600; transition: all .15s; }
.btn-ok:hover { background: linear-gradient(135deg, #3a7bc8, #4a7de0); box-shadow: 0 2px 8px rgba(74,144,217,.3); }
</style>
